/**
 * iter162 — Global axios + unhandled-rejection safety net.
 *
 * The codebase has 40+ files that do `toast.error(err.response?.data?.detail || fallback)`.
 * The Phase 19C preview-readonly middleware (and a few other endpoints) returns
 * `{detail: {code, message}}` which, when passed straight to `toast.error()`,
 * is rendered as a React child and crashes the app:
 *   "Objects are not valid as a React child (found: object with keys {code, message})".
 *
 * Auditing & rewriting every call site is impractical and brittle. We patch the
 * problem at the axios layer once:
 *   - If `err.response.data.detail` is an object, replace it with a safe string
 *     (preserving the original payload on `_detailMeta` for any FE code that
 *     branches on `detail.code`).
 *   - If `detail` is an array (FastAPI 422), join the messages.
 *
 * We also install a `window.unhandledrejection` handler that suppresses CRA's
 * red dev-overlay for unhandled AxiosError rejections (a 403 from a missing
 * `view_customers` perm shouldn't blow up the whole UI). The error is still
 * `console.error`'d so it stays visible during debugging.
 *
 * Import this module ONCE from App.js (top-level side effect).
 */
import axios from 'axios';

let installed = false;

export function installGlobalErrorSafetyNet() {
  if (installed) return;
  installed = true;

  // 1) Axios response interceptor — coerce `data.detail` into a render-safe string.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const data = error?.response?.data;
        if (data && typeof data === 'object') {
          const detail = data.detail;
          if (Array.isArray(detail)) {
            // FastAPI 422 — list of {loc, msg, type, input}
            const msgs = detail
              .map((d) => (d && typeof d === 'object' ? d.msg || d.message : String(d)))
              .filter(Boolean);
            error.response.data = { ...data, detail: msgs.join('; ') || 'Validation error', _detailMeta: detail };
          } else if (detail && typeof detail === 'object') {
            // e.g. Phase 19C preview-readonly: {code, message}
            const msg = detail.message || detail.msg || detail.error || 'Request failed';
            error.response.data = { ...data, detail: String(msg), _detailMeta: detail };
          }
        }
      } catch (_normaliseErr) {
        // Never let the interceptor itself blow up.
      }
      return Promise.reject(error);
    },
  );

  // 2) Unhandled-rejection handler — keep CRA's red overlay from breaking the
  //    user's flow when a fetch fails (e.g. office_staff viewing /admin/customers
  //    without `view_customers`). The error is still logged; we just don't crash.
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      const isAxios = reason && (reason.isAxiosError === true || reason?.name === 'AxiosError' || reason?.config);
      if (isAxios) {
        console.warn('[unhandled axios rejection suppressed]', reason?.response?.status, reason?.config?.url, reason?.message);
        event.preventDefault();
      }
    });
  }
}

export default installGlobalErrorSafetyNet;
